import { apiError } from "@/lib/api";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Leaf, Headset, Sparkles } from "lucide-react";

const FOREST_BG = "https://static.prod-images.emergentagent.com/jobs/1c220779-6edd-4bd3-b2c7-23a9d0486762/images/30772e068c67c998a3f9d7e1635210799fdcb80e5fd48302cae8ec811f38af6c.jpeg";

export default function Login() {
  const { loginJwt } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginJwt(email, password);
      toast.success("Welcome back");
      nav("/ecosystem");
    } catch (err) {
      toast.error(apiError(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/ecosystem";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={FOREST_BG} alt="" className="absolute inset-0 w-full h-full object-cover eco-bg" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-ink-900/30 to-ink-900/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-magenta to-brand-amber grid place-items-center shadow-glow-magenta">
              <Leaf className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight" data-testid="login-wizlife-logo">
              WIZ<span className="gradient-text">LIFE</span>
            </span>
          </div>
          <div>
            <div className="label-mono text-brand-magenta">// step_into_the_metaverse</div>
            <h2 className="font-display font-black text-4xl xl:text-5xl leading-tight tracking-tighter mt-3">
              ONE LOGIN.
              <br /> A WHOLE <span className="text-brand-magenta glow-green">WORLD</span>.
            </h2>
            <p className="text-white/70 font-body mt-4 max-w-md">
              Business suites, games, shopping and sports — one living forest ecosystem, all powered by USDT.
            </p>
            <div className="flex items-center gap-2 mt-6 label-mono text-white/60">
              <Headset className="w-4 h-4 text-brand-cyan" /> VR headset ready · WebXR
            </div>
          </div>
          <div className="label-mono text-white/40">wizlife metaverse ecosystem</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 scanlines relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-magenta to-brand-amber grid place-items-center">
              <Leaf className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-extrabold text-lg">WIZ<span className="gradient-text">LIFE</span></span>
          </div>

          <div className="label-mono text-brand-magenta mb-2">// citizen_login</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-6">WELCOME BACK.</h1>

          <form onSubmit={submit} className="panel p-6 space-y-4" data-testid="auth-form">
            <div>
              <label className="label-mono">Email</label>
              <input type="email" className="field mt-2" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="auth-email-input" />
            </div>
            <div>
              <label className="label-mono">Password</label>
              <input type="password" className="field mt-2" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="auth-password-input" />
            </div>
            <button type="submit" disabled={busy} className="btn-neon w-full" data-testid="auth-submit-button">
              {busy ? "…" : "ENTER THE ECOSYSTEM →"}
            </button>
            <button type="button" onClick={google} className="btn-ghost w-full flex items-center justify-center gap-2" data-testid="google-login-button">
              <Sparkles className="w-4 h-4" /> Continue with Google
            </button>
          </form>

          <p className="text-center text-white/40 text-xs font-mono mt-5">
            New here? <Link to="/register" className="text-brand-magenta hover:underline" data-testid="login-register-link">Create your account</Link>
          </p>
          <p className="text-center text-white/30 text-[11px] font-mono mt-2">By continuing you agree to our terms.</p>
        </div>
      </div>
    </div>
  );
}
